import { CommentsQueryDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import {
  PaginatedQueryConfig,
  PaginationParams,
  PaginationResponse,
  usePaginatedQuery,
  UsePaginatedQueryOptions,
} from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';

import { api } from '@/lib/api-client';
import { CommentEntity } from '@/types/api';

export type GetCommentsParams = CommentsQueryDto & PaginationParams;

export const getComments = async (
  orgId: string,
  projectId: string,
  params: GetCommentsParams,
): Promise<PaginationResponse<CommentEntity>> => {
  const { data } = await api.get<undefined, ApiResponse<PaginationResponse<CommentEntity>>>(
    `/orgs/${orgId}/projects/${projectId}/comments`,
    {
      params,
    },
  );

  return data;
};

export const getCommentsQueryKey = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  filters?: CommentsQueryDto | null,
) => {
  const queryKey: unknown[] = ['comments'];
  if (orgId) queryKey.push('org', orgId);
  if (projectId) queryKey.push('project', projectId);
  if (filters) queryKey.push('filters', filters);
  return queryKey;
};

export const getCommentsQueryOptions = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  filters: CommentsQueryDto | undefined | null,
): UsePaginatedQueryOptions<CommentEntity> => {
  return {
    queryKey: getCommentsQueryKey(orgId, projectId, filters),
    queryFn: ({ pageParam }) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getComments(orgId!, projectId!, {
        ...filters,
        ...pageParam,
      }),
    enabled: !!orgId && !!projectId,
  };
};

interface UseCommentsOptions {
  queryConfig?: PaginatedQueryConfig<typeof getCommentsQueryOptions>;
}

export const useComments = (
  filters?: CommentsQueryDto | null,
  { queryConfig }: UseCommentsOptions = {},
) => {
  const { orgId, projectId } = useScopeIds();
  return usePaginatedQuery({
    ...getCommentsQueryOptions(orgId, projectId, filters),
    ...queryConfig,
  });
};
